import { mutation, query } from "gql-query-builder";
import type IQueryBuilderOptions from "gql-query-builder/build/IQueryBuilderOptions";

import { moneyFields, pageFields } from "./fields";
import {
  automaticDiscountApplicationFragment,
  manualDiscountApplicationFragment,
} from "./fragments";
import {
  birthMetafield,
  cardInfoMetafield,
  genderMetafield,
} from "./metafields/customer-metafields";
import {
  brandStoreOrderIdMetafield,
  drawStatusMetafield,
  featuredImageMetafield,
  giftingIsAcceptedMetafield,
  giftingMessageTemplateMetafield,
  giftingTokenMetafield,
  instagramMetafield,
  isFamilySaleMetafield,
  isOfflineMetafield,
  productCodeMetafield,
  returnStatusMetafield,
  scoreMetafield,
  serialNumberMetafield,
} from "./metafields/order-metafields";
import { releaseDateMetafield } from "./metafields/product-metafields";

/**
 * Customer Account API 전용 쿼리/뮤테이션.
 * https://shopify.dev/docs/api/customer/latest/objects/Customer
 *
 * Storefront API customer-gql와 주요 스키마 차이:
 *   email                  → emailAddress.emailAddress
 *   phone                  → phoneNumber.phoneNumber
 *   createdAt              → creationDate
 *   countryCode / province → territoryCode / zoneCode (CustomerAddressInput)
 *   customerUserErrors     → userErrors (shape은 { code, field, message } 동일)
 *
 * 누락 mutation:
 *   customerDefaultAddressUpdate — CAA엔 없음.
 *     customerAddressCreate/Update의 defaultAddress: Boolean 인자로 합쳐짐.
 */

// ─── Fields ────────────────────────────────────────────────────────────

/**
 * classic Storefront API의 DiscountCodeApplication은 `applicable` 필드가 있지만
 * CAA 스키마엔 없어(https://shopify.dev/docs/api/customer/latest/objects/DiscountCodeApplication)
 * fragments.ts의 discountCodeApplicationFragment를 그대로 재사용하지 못하고 별도 정의함.
 */
const caaDiscountCodeApplicationFragment = {
  operation: "DiscountCodeApplication",
  fields: ["code"],
  fragment: true,
};

export const caaCustomerAddressFields: IQueryBuilderOptions["fields"] = [
  "address1",
  "address2",
  "city",
  "company",
  "country",
  "firstName",
  "formatted",
  "formattedArea",
  "id",
  "lastName",
  "name",
  "phoneNumber",
  "province",
  "territoryCode",
  "zip",
  "zoneCode",
];

/**
 * Customer > Order LineItem.
 *
 * CAA LineItem은 classic의 OrderLineItem과 스키마 차이가 큼:
 *   - classic: `variant: ProductVariant & { product: Product }` 중첩 객체로 풀어 받음
 *   - CAA: nested variant/product 없음.
 *       title           = product title (classic의 lineItem.title == variant.product.title 과 동일)
 *       productId       = product GID
 *       productType     = product type
 *       variantId       = variant GID
 *       variantTitle    = variant title
 *     필요한 경우 productId로 Storefront product 쿼리를 별도 호출.
 */
export const caaLineItemFields: IQueryBuilderOptions["fields"] = [
  "id",
  "title",
  "quantity",
  "refundableQuantity",
  "variantId",
  "variantTitle",
  "productId",
  "productType",
  { image: ["url", "altText"] },
  { totalPrice: moneyFields },
  { currentTotalPrice: moneyFields },
  { totalDiscount: moneyFields },
  {
    discountAllocations: [
      {
        allocatedAmount: moneyFields,
        discountApplication: [
          manualDiscountApplicationFragment,
          automaticDiscountApplicationFragment,
          caaDiscountCodeApplicationFragment,
        ],
      },
    ],
  },
];

/**
 * Return.returnLineItems 항목.
 * 반품 요청 상세(수량, 사유)와 Orders의 lineItem 매칭해서 `currentQuantity`(환불 반영 후 수량) 계산
 */
export const caaReturnLineItemFields: IQueryBuilderOptions["fields"] = [
  "id",
  "quantity",
  "returnReason",
  { lineItem: ["id"] },
];

/**
 * Return.
 * status로 반품 진행 상태(REQUESTED / OPEN / CLOSED / CANCELED / DECLINED) 파악 가능.
 */
export const caaReturnFields: IQueryBuilderOptions["fields"] = [
  "id",
  "name",
  "status",
  {
    operation: "returnLineItems(first: 20)",
    fields: [{ edges: [{ node: caaReturnLineItemFields }] }],
    variables: {},
  },
];

/** Fulfillment.trackingInformation 항목. classic의 fulfillmentFields(trackingInfo)에 대응. */
export const caaTrackingInformationFields: IQueryBuilderOptions["fields"] = [
  "company",
  "number",
  "url",
];

export const caaFulfillmentFields: IQueryBuilderOptions["fields"] = [
  "id",
  { trackingInformation: caaTrackingInformationFields },
];

export const caaOrderFields: IQueryBuilderOptions["fields"] = [
  "id",
  "name",
  "number",
  "processedAt",
  "createdAt",
  "updatedAt",
  "cancelReason",
  "financialStatus",
  "fulfillmentStatus",
  "statusPageUrl",
  "currencyCode",
  { totalPrice: moneyFields },
  { totalRefunded: moneyFields },
  { totalShipping: moneyFields },
  { subtotal: moneyFields },
  { totalTax: moneyFields },
  { shippingAddress: caaCustomerAddressFields },
  { billingAddress: caaCustomerAddressFields },
  {
    operation: "lineItems(first: 100)",
    fields: [{ edges: [{ node: caaLineItemFields }] }],
    variables: {},
  },
  {
    operation: "returns(first: 10)",
    fields: [{ edges: [{ node: caaReturnFields }] }],
    variables: {},
  },
  {
    operation: "fulfillments(first: 10)",
    fields: [{ edges: [{ node: caaFulfillmentFields }] }],
    variables: {},
  },
  drawStatusMetafield,
  featuredImageMetafield,
  isOfflineMetafield,
  returnStatusMetafield,
  instagramMetafield,
  serialNumberMetafield,
  scoreMetafield,
  productCodeMetafield,
  cardInfoMetafield,
  isFamilySaleMetafield,
  brandStoreOrderIdMetafield,
  giftingIsAcceptedMetafield,
  giftingMessageTemplateMetafield,
  giftingTokenMetafield,
  releaseDateMetafield,
];

export const caaCustomerFields: IQueryBuilderOptions["fields"] = [
  "id",
  "firstName",
  "lastName",
  "displayName",
  "creationDate",
  "tags",
  { emailAddress: ["emailAddress", "marketingState"] },
  { phoneNumber: ["phoneNumber"] },
  { defaultAddress: caaCustomerAddressFields },
  {
    operation: "addresses(first: 50)",
    fields: [{ edges: [{ node: caaCustomerAddressFields }] }],
    variables: {},
  },
  // orders는 여기서 같이 요청하지 않는다 — first 인자 기준으로 쿼리 비용이 계산되어(lineItems/returns/fulfillments까지 중첩)
  // 세션 확인/프로필 조회 같은 가벼운 호출에서마다 비용 한도를 초과해 정상 로그인도 세션 만료로 오판되는 원인이 됐음.
  // 주문 목록은 별도로 페이지네이션되는 caaCustomerOrdersQuery(useOrders.ts)를 사용할 것.
  genderMetafield,
  birthMetafield,
  cardInfoMetafield,
  // TODO: wishListMetafield는 references 내부 `... on Product`가 Storefront Product 타입을 요구해서 그대로 사용 불가
];

export const caaUserErrorFields: IQueryBuilderOptions["fields"] = [
  "code",
  "field",
  "message",
];

// ─── Types ─────────────────────────────────────────────────────────────

export interface ICaaMoney {
  amount: string;
  currencyCode: string;
}

export interface ICaaMetafield {
  key: string;
  type: string;
  value: string;
  namespace: string;
}

export interface ICaaImage {
  url: string;
  altText?: string | null;
}

/**
 * LineItem.discountAllocations.discountApplication.
 * 타입명은 classic Storefront API와 동일해서(AutomaticDiscountApplication 등) fragments.ts의
 * manual/automaticDiscountApplicationFragment는 그대로 재사용하지만, DiscountCodeApplication은
 * CAA 스키마에 `applicable` 필드가 없어 caaDiscountCodeApplicationFragment(code만)를 따로 쓴다.
 */
export type TCaaDiscountApplication =
  | { __typename: "AutomaticDiscountApplication"; title: string }
  | {
      __typename: "ManualDiscountApplication";
      title: string;
      description?: string | null;
    }
  | { __typename: "DiscountCodeApplication"; code: string };

export interface ICaaDiscountAllocation {
  allocatedAmount: ICaaMoney;
  discountApplication: TCaaDiscountApplication;
}

export interface ICaaLineItem {
  /** LineItem GID. Refund.refundLineItems가 이 id로 참조. */
  id: string;
  /** product title (classic의 lineItem.title과 동일) */
  title: string;
  quantity: number;
  /** quantity에서 환불된 수량을 뺀 값 (Shopify Customer Account API 정의) */
  refundableQuantity: number;
  variantId?: string | null;
  variantTitle?: string | null;
  productId?: string | null;
  productType?: string | null;
  image?: ICaaImage | null;
  totalPrice?: ICaaMoney | null;
  currentTotalPrice?: ICaaMoney | null;
  totalDiscount?: ICaaMoney | null;
  discountAllocations: ICaaDiscountAllocation[];
}

/** Return.status enum. */
export type TCaaReturnStatus =
  | "REQUESTED"
  | "OPEN"
  | "CLOSED"
  | "CANCELED"
  | "DECLINED";

/** Order.returns[].returnLineItems[] 항목. */
export interface ICaaReturnLineItem {
  id: string;
  quantity: number;
  /** 사용자가 선택한 반품 사유 (Shopify enum) */
  returnReason?: string | null;
  lineItem?: { id: string } | null;
}

export interface ICaaReturn {
  id: string;
  name: string;
  status: TCaaReturnStatus;
  returnLineItems: {
    edges: { node: ICaaReturnLineItem }[];
  };
}

/** Fulfillment.trackingInformation. classic의 ITrackingInfo(number/url)에 company가 추가된 형태. */
export interface ICaaTrackingInformation {
  company?: string | null;
  number?: string | null;
  url?: string | null;
}

export interface ICaaFulfillment {
  id: string;
  trackingInformation: ICaaTrackingInformation[];
}

export type TCaaOrderFinancialStatus =
  | "AUTHORIZED"
  | "EXPIRED"
  | "PAID"
  | "PARTIALLY_PAID"
  | "PARTIALLY_REFUNDED"
  | "PENDING"
  | "REFUNDED"
  | "VOIDED";

export type TCaaOrderFulfillmentStatus =
  | "FULFILLED"
  | "IN_PROGRESS"
  | "ON_HOLD"
  | "OPEN"
  | "PARTIALLY_FULFILLED"
  | "PENDING_FULFILLMENT"
  | "RESTOCKED"
  | "SCHEDULED"
  | "UNFULFILLED";

export interface ICaaOrder {
  id: string;
  name: string;
  number: number;
  processedAt: string;
  createdAt: string;
  updatedAt: string;
  cancelReason?: string | null;
  financialStatus?: TCaaOrderFinancialStatus | null;
  fulfillmentStatus: TCaaOrderFulfillmentStatus;
  statusPageUrl: string;
  currencyCode: string;
  totalPrice: ICaaMoney;
  totalRefunded: ICaaMoney;
  totalShipping: ICaaMoney;
  subtotal?: ICaaMoney | null;
  totalTax?: ICaaMoney | null;
  shippingAddress?: ICaaCustomerAddress | null;
  billingAddress?: ICaaCustomerAddress | null;
  lineItems: {
    edges: { node: ICaaLineItem }[];
  };
  /**
   * 반품 요청 내역. status로 진행 상태 파악 가능 (REQUESTED / OPEN / CLOSED / CANCELED / DECLINED).
   * returnStatus metafield에 의존하지 않는 native 반품 UX 구현 시에도 사용.
   */
  returns?: {
    edges: { node: ICaaReturn }[];
  } | null;
  /** 배송 tracking number/url 원본. classic의 successfulFulfillments에 대응 — getTrackingInfoCaa로 가공해서 사용. */
  fulfillments?: {
    edges: { node: ICaaFulfillment }[];
  } | null;
  // Order-level metafield 별칭 — classic의 IOrdersEdgesNode와 동일 키로 정렬.
  // 같은 namespace/key에 데이터가 세팅된 운영 가정이라 콜사이트 코드가 그대로 재사용 가능.
  drawStatus?: ICaaMetafield | null;
  featuredImage?: ICaaMetafield | null;
  isOffline?: ICaaMetafield | null;
  returnStatus?: ICaaMetafield | null;
  instagram?: ICaaMetafield | null;
  serialNumber?: ICaaMetafield | null;
  score?: ICaaMetafield | null;
  productCode?: ICaaMetafield | null;
  cardInfo?: ICaaMetafield | null;
  isfamilysale?: ICaaMetafield | null;
  brandStoreOrderIds?: ICaaMetafield | null;
  giftingIsAccepted?: ICaaMetafield | null;
  giftingMessageTemplate?: ICaaMetafield | null;
  giftingToken?: ICaaMetafield | null;
  releaseDate?: ICaaMetafield | null;
}

export interface ICaaCustomer {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  creationDate?: string | null;
  tags: string[];
  emailAddress?: {
    emailAddress: string;
    marketingState?: TCaaEmailMarketingState;
  } | null;
  phoneNumber?: { phoneNumber: string } | null;
  defaultAddress?: ICaaCustomerAddress | null;
  addresses?: {
    edges: { node: ICaaCustomerAddress }[];
  };
  orders?: {
    edges: { node: ICaaOrder }[];
  };
  /**
   * customer-metafields 헬퍼의 alias 그대로 매핑.
   * gender: metafield(namespace="info", key="gender") 등
   */
  gender?: ICaaMetafield | null;
  birth?: ICaaMetafield | null;
  cardInfo?: ICaaMetafield | null;
}

export interface ICaaCustomerAddress {
  id: string;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  company?: string | null;
  /** 국가 명칭 (display) */
  country?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  /** "성+이름" 같은 displayable name */
  name?: string | null;
  phoneNumber?: string | null;
  /** province / state 명칭 (display) */
  province?: string | null;
  /** ISO 2자 국가 코드 (classic의 countryCodeV2 대응) */
  territoryCode?: string | null;
  zip?: string | null;
  /** province / state 코드 (classic의 provinceCode 대응) */
  zoneCode?: string | null;
  /** Shopify가 줄바꿈으로 끊어둔 주소 라인 배열 */
  formatted?: string[] | null;
  /** "city, province zip" 같은 한 줄 요약 */
  formattedArea?: string | null;
}

export interface ICaaUserError {
  code?: string | null;
  field?: string[] | null;
  message: string;
}

export interface ICaaCustomerQuery {
  customer: ICaaCustomer | null;
}

/**
 * CAA orders 페이지네이션 쿼리 응답.
 * classic의 ICustomerOrdersQuery와 shape 동일 (customer.orders.{edges, pageInfo}).
 * OrderHistory 페이지에서 fetchMore(after) 반복해 스크롤 로드.
 */
export interface ICaaCustomerOrdersQuery {
  customer: {
    orders: {
      edges: { node: ICaaOrder }[];
      pageInfo: {
        hasNextPage: boolean;
        hasPreviousPage: boolean;
        endCursor: string | null;
        startCursor: string | null;
      };
    };
  } | null;
}

/** 단건 주문 조회(caaOrderQuery) 응답. */
export interface ICaaOrderQuery {
  order: ICaaOrder | null;
}

export interface ICaaCustomerUpdateMutation {
  customerUpdate: {
    customer: ICaaCustomer | null;
    userErrors: ICaaUserError[];
  };
}

export interface ICaaCustomerAddressCreateMutation {
  customerAddressCreate: {
    customerAddress: ICaaCustomerAddress | null;
    userErrors: ICaaUserError[];
  };
}

export interface ICaaCustomerAddressUpdateMutation {
  customerAddressUpdate: {
    customerAddress: ICaaCustomerAddress | null;
    userErrors: ICaaUserError[];
  };
}

export interface ICaaCustomerAddressDeleteMutation {
  customerAddressDelete: {
    deletedAddressId: string | null;
    userErrors: ICaaUserError[];
  };
}

export type TCaaEmailMarketingState =
  | "INVALID"
  | "NOT_SUBSCRIBED"
  | "PENDING"
  | "REDACTED"
  | "SUBSCRIBED"
  | "UNSUBSCRIBED";

export interface ICaaCustomerEmailAddress {
  emailAddress: string;
  marketingState: TCaaEmailMarketingState;
}

export interface ICaaCustomerEmailMarketingSubscribeMutation {
  customerEmailMarketingSubscribe: {
    emailAddress: ICaaCustomerEmailAddress | null;
    userErrors: ICaaUserError[];
  };
}

export interface ICaaCustomerEmailMarketingUnsubscribeMutation {
  customerEmailMarketingUnsubscribe: {
    emailAddress: ICaaCustomerEmailAddress | null;
    userErrors: ICaaUserError[];
  };
}

// ─── Operations ────────────────────────────────────────────────────────

/**
 * 회원 정보 조회
 * CAA는 인증을 Authorization 헤더로 처리하므로 customer(...) 인자 없음.
 */
export const caaCustomerQuery = query(
  {
    operation: "customer",
    fields: caaCustomerFields,
  },
  undefined,
  { operationName: "CaaCustomer" },
);

/**
 * OrderHistory 페이지용 orders 페이지네이션 쿼리.
 * classic customerOrdersQuery와 shape 정렬 (edges/pageInfo).
 * 인증은 caaClient의 Authorization 헤더에서 처리되므로 top-level 인자 없음.
 */
export const caaCustomerOrdersQuery = query(
  {
    operation: "customer",
    fields: [
      {
        operation: "orders",
        fields: [
          { edges: [{ node: caaOrderFields }] },
          { pageInfo: pageFields },
        ],
        variables: {
          first: { type: "Int" },
          sortKey: { type: "OrderSortKeys" },
          reverse: { type: "Boolean" },
          after: { type: "String" },
        },
      },
    ],
  },
  undefined,
  { operationName: "CaaCustomerOrders" },
);

/**
 * 단건 주문 조회. classic엔 없는 CAA 전용 root query — customer.orders 목록을 다 불러와서
 * id로 찾던 useFindOrderCaa의 우회를 대체하기 위한 시도.
 * 주의: Shopify 문서상 이 필드의 access scope가 customer_read_unauthenticated /
 * customer_read_pre_authenticated / customer_read_payment_instrument_authenticated로 명시되어 있어
 * (orders 조회에 쓰는 customer_read_orders와 다른 계열) 이 앱이 쓰는 OAuth `customer-account-api:full`
 * 토큰으로 실제 호출이 통과되는지 검증 필요 — 안 되면 useFindOrderCaa는 기존 방식으로 되돌릴 것.
 */
export const caaOrderQuery = query(
  {
    operation: "order",
    fields: caaOrderFields,
    variables: { id: { type: "ID!" } },
  },
  undefined,
  { operationName: "CaaOrder" },
);

/**
 * 회원 정보 수정
 * CAA의 CustomerUpdateInput은 firstName / lastName 만 받음.
 */
export const caaCustomerUpdateMutation = mutation(
  {
    operation: "customerUpdate",
    fields: [
      { customer: caaCustomerFields },
      { userErrors: caaUserErrorFields },
    ],
    variables: { input: { type: "CustomerUpdateInput!" } },
  },
  undefined,
  { operationName: "CaaCustomerUpdate" },
);

/**
 * 배송지 생성
 * defaultAddress: true로 호출하면 생성과 동시에 기본 배송지로 지정
 */
export const caaCustomerAddressCreateMutation = mutation(
  {
    operation: "customerAddressCreate",
    fields: [
      { customerAddress: caaCustomerAddressFields },
      { userErrors: caaUserErrorFields },
    ],
    variables: {
      address: { type: "CustomerAddressInput!" },
      defaultAddress: { type: "Boolean" },
    },
  },
  undefined,
  { operationName: "CaaCustomerAddressCreate" },
);

/**
 * 배송지 수정
 * defaultAddress: true로 호출하면 수정과 동시에 기본 배송지로 지정
 * (classic의 customerDefaultAddressUpdate를 대체)
 */
export const caaCustomerAddressUpdateMutation = mutation(
  {
    operation: "customerAddressUpdate",
    fields: [
      { customerAddress: caaCustomerAddressFields },
      { userErrors: caaUserErrorFields },
    ],
    variables: {
      addressId: { type: "ID!" },
      address: { type: "CustomerAddressInput" },
      defaultAddress: { type: "Boolean" },
    },
  },
  undefined,
  { operationName: "CaaCustomerAddressUpdate" },
);

/**
 * 배송지 삭제
 */
export const caaCustomerAddressDeleteMutation = mutation(
  {
    operation: "customerAddressDelete",
    fields: ["deletedAddressId", { userErrors: caaUserErrorFields }],
    variables: { addressId: { type: "ID!" } },
  },
  undefined,
  { operationName: "CaaCustomerAddressDelete" },
);

const caaCustomerEmailAddressFields: IQueryBuilderOptions["fields"] = [
  "emailAddress",
  "marketingState",
];

/**
 * 이메일 마케팅 구독
 * CAA는 인증된 customer 컨텍스트에서 동작 — input 인자 없이 현재 customer의 email을 구독 처리.
 * (spec상 input 인자가 추가될 경우 variables 채워서 보강 필요)
 */
export const caaCustomerEmailMarketingSubscribeMutation = mutation(
  {
    operation: "customerEmailMarketingSubscribe",
    fields: [
      { emailAddress: caaCustomerEmailAddressFields },
      { userErrors: caaUserErrorFields },
    ],
  },
  undefined,
  { operationName: "CaaCustomerEmailMarketingSubscribe" },
);

/**
 * 이메일 마케팅 구독 해지
 */
export const caaCustomerEmailMarketingUnsubscribeMutation = mutation(
  {
    operation: "customerEmailMarketingUnsubscribe",
    fields: [
      { emailAddress: caaCustomerEmailAddressFields },
      { userErrors: caaUserErrorFields },
    ],
  },
  undefined,
  { operationName: "CaaCustomerEmailMarketingUnsubscribe" },
);
